import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  HostListener,
  TemplateRef,
  inject
} from '@angular/core';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { FormArray, FormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import {
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig,
  AlertType,
  AlertService,
  Alert,
  ModalResult,
  ModalComponent
} from '@michelin/theme';
import { Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, map, Observable } from 'rxjs';
import { TranslationState } from 'src/app/shared/state/TranslationState';
import { ProductDataService } from '../../services/product-data.service';
import { MakeInfo } from '../../models/MakeInfo';
import { Make, MakeFiltersResponse, PaginatedMakeInfo, PaginatedMakeResponse, Pagination } from '../../models/response.model';
import { ActivatedRoute } from '@angular/router';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';

@Component({
  selector: 'app-make-screen',
  templateUrl: './make-screen.component.html',
  styleUrls: ['./make-screen.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MakeComponent implements OnInit {
  @Input() makeInfo: MakeInfo;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  public fb = inject(UntypedFormBuilder);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public store = inject(Store);
  public cdr = inject(ChangeDetectorRef);
  public alertService = inject(AlertService);
  public route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  makeList: Make[] = [];
  pagination: Pagination;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 15, 25, 50];
  codeList: string[] = [];
  descriptionList: string[] = [];
  tierList: string[] = [];
  rollPartNumberList: string[] = [];
  finishedPartNumberList: string[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  productSaveDisabled = true;
  saveEnabled = false;
  isModified = false;

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-Make-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  tableConfig: TableConfig<Make> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    columns: {}
  };
  state = TableConfig.getState(this.tableConfig, this.route);

  fetcher: PaginatorFetcher<Make> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    return this.productDataService.getMakeLines({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((makeResponse: PaginatedMakeResponse) => {
        this.makeList = makeResponse.data.makes;
        this.pagination = makeResponse.data.pagination;
        this.updateDisplayedRowsForm(this.makeList);
        this.displayedRowsForm.markAsPristine();
        return {
          items: this.makeList,
          count: this.pagination.totalRecords
        };
      })
    );
  };
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  columnNames: Record<string, string> = {};

  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getMakeLines(this.filterFormBackend.value, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.makes as Make[]).map((item) => ({
          make: item.make,
          description: item.description,
          tier: item.tier,
          dealerRMVirginSuffixNo: item.dealerRMVirginSuffixNo,
          dealerRMNonVirginSuffixNo: item.dealerRMNonVirginSuffixNo,
          dealerFGVirginSuffixNo: item.dealerFGVirginSuffixNo,
          dealerFGNonVirginSuffixNo: item.dealerFGNonVirginSuffixNo
        }))
      }))
    );

  constructor() {
    this.descriptionList = this.store.selectSnapshot(ProductsState.products).makes;
    this.rollPartNumberList = this.store.selectSnapshot(ProductsState.products).rollPartNumbers;
    this.filterForm = this.fb.group({
      codes: [[]],
      descriptions: [[]],
      tiers: [[]]
    });
    this.filterFormBackend = this.fb.group({
      codes: [[]],
      descriptions: [[]],
      tiers: [[]]
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.productDataService.getMakeFilters().subscribe((response: MakeFiltersResponse) => {
      this.codeList = response.data.makeCodes || [];
      this.tierList = response.data.tiers || [];
    });
    this.setupFormListeners();
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.isModified = this.checkIfModified();
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.columnNames = {
        make: this.translationService.translate('makeText'),
        description: this.translationService.translate('descriptionText'),
        tier: this.translationService.translate('tierText'),
        dealerRMVirginSuffixNo: this.translationService.translate('dealerRMVirginSuffixText'),
        dealerRMNonVirginSuffixNo: this.translationService.translate('dealerRMNonVirginSuffixText'),
        dealerFGVirginSuffixNo: this.translationService.translate('dealerFGVirginSuffixText'),
        dealerFGNonVirginSuffixNo: this.translationService.translate('dealerFGNonVirginSuffixText')
      };

      this.tableConfig.columns = {
        makeCode: { cell: (elm: Make) => elm.make, header: this.translationService.translate('makeText') },
        makeDesc: { cell: (elm: Make) => elm.description, header: this.translationService.translate('descriptionText') },
        tier: { cell: 'tier', header: this.translationService.translate('tierText') },
        rmSuffixVirgin: { cell: 'rmVirginDropdown', header: this.translationService.translate('dealerRMVirginSuffixText') },
        rmSuffixNonVirgin: { cell: 'rmSuffixNonVirgin', header: this.translationService.translate('dealerRMNonVirginSuffixText') },
        fgSuffixVirgin: { cell: 'fgVirginDropdown', header: this.translationService.translate('dealerFGVirginSuffixText') },
        fgSuffixNonVirgin: { cell: 'fgNonVirginDropdown', header: this.translationService.translate('dealerFGNonVirginSuffixText') }
      };
      this.cdr.markForCheck();
    });
    this.updateDropdownLists();
  }

  checkIfModified(): boolean {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    let modified = false;
    for (let i = 0; i < this.makeList.length; i++) {
      const original = this.makeList[i] as any;
      const current = formArray.at(i).value;
      if (Object.keys(current).some((key) => (current[key] ?? '') !== (original[key] ?? ''))) {
        modified = true;
        break;
      }
    }
    return modified;
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      codes: this.filterForm.get('codes')?.value || [],
      descriptions: this.filterForm.get('descriptions')?.value || [],
      tiers: this.filterForm.get('tiers')?.value || []
    });
  }

  resetFilters(): void {
    this.filterForm.reset();
  }

  private setupFormListeners(): void {
    this.filterForm.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe(() => {
      this.generateFilterForm();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    });
  }

  updateDropdownLists(): void {
    this.finishedPartNumberList.push(...this.store.selectSnapshot(ProductsState.products).finishedPartNumbers);
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.makeList.length; i++) {
      const original = this.makeList[i];
      const current = formArray.at(i).value;

      const isModified = Object.keys(current).some((key) => current[key] !== original[key]);
      if (isModified) {
        modifiedRows.push({ ...original, ...current });
      }
    }
    if (modifiedRows.length > 0) {
      const payload: PaginatedMakeInfo = {
        makes: modifiedRows,
        pagination: this.pagination
      };
      this.productDataService.updateMakes(payload).subscribe({
        next: (response: PaginatedMakeResponse) => {
          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('camSaveSuccessText')
          } as Alert);
          if (response && response.data && response.data.makes) {
            for (const updatedRow of response.data.makes) {
              const idx = this.makeList.findIndex((row) => row.casingMakePk === updatedRow.casingMakePk);
              if (idx !== -1) {
                this.makeList[idx] = { ...this.makeList[idx], ...updatedRow };
              }
            }
          }
          this.updateDisplayedRowsForm(this.makeList);
          this.displayedRowsForm.markAsPristine();
          this.isModified = false;
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
        }
      });
    } else {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
    }
  }

  public updateDisplayedRowsForm(rows: Make[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  createRowForm(row: Make): FormGroup {
    return this.fb.group({
      tier: [row.tier],
      dealerRMVirginSuffixNo: [row.dealerRMVirginSuffixNo ?? null],
      dealerRMNonVirginSuffixNo: [row.dealerRMNonVirginSuffixNo ?? null],
      dealerFGVirginSuffixNo: [row.dealerFGVirginSuffixNo ?? null],
      dealerFGNonVirginSuffixNo: [row.dealerFGNonVirginSuffixNo ?? null]
    });
  }

  getControl(item: Make, controlName: keyof Make): FormControl {
    const index = this.makeList.findIndex((row) => row.make === item.make);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.updateDisplayedRowsForm(this.makeList);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      }
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.checkIfModified()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.checkIfModified()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }
}
